import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export default function UserAnalyticsTab({ loading, userAnalytics }) {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-48">
                <ArrowPathIcon className="h-7 w-7 text-gray-950 animate-spin" />
                <p className="text-xs text-gray-500 mt-2.5 font-bold uppercase tracking-wider">Fetching User Analytics...</p>
            </div>
        );
    }

    if (!userAnalytics) {
        return (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <p className="text-sm font-semibold uppercase tracking-wider">No Analytics Data Available</p>
            </div>
        );
    }

    const formatCurrency = (val) => {
        if (val === undefined || val === null || val === "N/A") return "N/A";
        const num = Number(val);
        if (isNaN(num)) return val;
        return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === "N/A") return "N/A";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
            });
        } catch (e) {
            return dateStr;
        }
    };

    const d = userAnalytics;
    const damageVal = Number(d.total_damage_charges) || 0;
    const damageInstances = Number(d.number_of_damage_instances) || 0;
    const savingsVal = Number(d.savings_past_2_years) || 0;
    const streakVal = Number(d.reading_streak) || 0;

    return (
        <div className="space-y-6 w-full text-xs">
            {/* Section 1: Membership Info */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 border-b pb-1.5">
                    Membership Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Subscriber Since</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{formatDate(d.subscriber_since)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg col-span-1 sm:col-span-2 md:col-span-1">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Membership Plan</p>
                        <p className="text-xs font-bold text-gray-900 mt-1 truncate" title={d.membership_plan}>{d.membership_plan || "N/A"}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Status</p>
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 ${String(d.subscription_status).toLowerCase() === "active"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-600 border border-gray-200"
                            }`}>
                            {d.subscription_status || "N/A"}
                        </span>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Renewal Date</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{formatDate(d.renewal_date)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Renewal Risk Flag</p>
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 ${d.renewal_risk_flag && d.renewal_risk_flag !== "N/A"
                            ? "bg-red-50 text-red-700 border border-red-200"
                            : "bg-gray-50 text-gray-500 border border-gray-200"
                            }`}>
                            {d.renewal_risk_flag || "N/A"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Section 2: Reading Stats */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 border-b pb-1.5">
                    Reading Statistics
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Books Issued</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{d.total_books_issued ?? 0}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Quarterly Issued</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{d.books_issued_quarterly ?? 0}</p>
                    </div>
                    <div className={`p-3 border rounded-lg transition-all duration-200 ${streakVal > 0
                        ? "bg-amber-50/40 border-amber-300 shadow-xs"
                        : "bg-white border-gray-200"
                        }`}>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1">
                            Reading Streak {streakVal > 0 && <span>🔥</span>}
                        </p>
                        <p className={`text-xl font-bold mt-1 ${streakVal > 0 ? "text-amber-600" : "text-gray-900"}`}>
                            {streakVal} days
                        </p>
                    </div>
                </div>
            </div>

            {/* Section 3: Financial Stats */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 border-b pb-1.5">
                    Financial Statistics
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-emerald-50/20 border border-emerald-300 p-3 rounded-lg shadow-xs col-span-1 sm:col-span-2 md:col-span-1">
                        <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Total Revenue</p>
                        <p className="text-xl font-bold text-emerald-800 mt-1">{formatCurrency(d.total_revenue_generated)}</p>
                    </div>
                    <div className={`p-3 border rounded-lg transition-all duration-200 ${damageVal > 0
                        ? "bg-red-50/40 border-red-300 shadow-xs"
                        : "bg-white border-gray-200"
                        }`}>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Damage Charges</p>
                        <p className={`text-sm font-bold mt-1 ${damageVal > 0 ? "text-red-600" : "text-gray-900"}`}>
                            {formatCurrency(d.total_damage_charges)}
                            {damageInstances > 0 && (
                                <span className="text-[10px] font-medium text-gray-400 block mt-0.5">
                                    ({damageInstances} instances)
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Overdue Charges</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.total_overdue_charges)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Membership Revenue</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.membership_revenue_generated)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Other Revenue</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.other_revenue_generated)}</p>
                    </div>
                </div>
            </div>

            {/* Section 4: Value Metrics */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 border-b pb-1.5">
                    Value Metrics
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Reading Value (Total)</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.total_reading_value_since_joining)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Reading Value (2y)</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.reading_value_past_2_years)}</p>
                    </div>
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Fees Paid (2y)</p>
                        <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(d.membership_fees_paid_past_2_years)}</p>
                    </div>
                    <div className={`p-3 border rounded-lg shadow-xs transition-all duration-200 ${savingsVal >= 0
                        ? "bg-green-50/40 border-green-300"
                        : "bg-red-50/30 border-red-200"
                        }`}>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Savings (2y)</p>
                        <p className={`text-sm font-bold mt-1 ${savingsVal >= 0 ? "text-green-700" : "text-red-600"}`}>
                            {formatCurrency(d.savings_past_2_years)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Section 5: Activity Info */}
            <div className="bg-white p-3 border border-gray-200 rounded-lg flex justify-between items-center">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Last Order Date</p>
                <p className="text-xs font-semibold text-gray-900">{formatDate(d.last_order_date)}</p>
            </div>
        </div>
    );
}
